import { useState } from "react";
import {
  CaretDown,
  CaretRight,
  CreditCard,
  Gift,
  Sparkle,
  Money,
  Receipt,
} from "@phosphor-icons/react";
import { Loader, MiniTile, Row, DeltaBadge, useReport, ExportCsvButton } from "../shared";

const TENDER_ICONS = {
  cash: Money,
  gift_card: Gift,
  rewards: Sparkle,
  other: Receipt,
};

export const TenderTypesSection = ({ filter }) => {
  const [data, loading, prevData] = useReport("/reports/tender-types", filter, false);
  const [cardOpen, setCardOpen] = useState(false);
  if (loading) return <Loader />;
  if (!data) return null;

  const card = data.card || {};
  const tenders = data.tenders || {};
  const salesTotal = data.sales_total || 0;
  const txnTotal = data.transactions_total || 0;
  const prevCard = prevData?.card || {};

  const declineRate = (() => {
    const tries = (card.approved_count || 0) + (card.declined_count || 0);
    if (!tries) return null;
    return (card.declined_count / tries) * 100;
  })();
  const prevDeclineRate = (() => {
    if (!prevData) return null;
    const tries = (prevCard.approved_count || 0) + (prevCard.declined_count || 0);
    if (!tries) return null;
    return (prevCard.declined_count / tries) * 100;
  })();

  const share = (n) => (salesTotal > 0 ? ((n / salesTotal) * 100).toFixed(1) : "0.0");
  const orderedKeys = ["cash", "gift_card", "rewards", "other"];

  return (
    <div className="space-y-4" data-testid="tenders-section">
      <div className="flex justify-end">
        <ExportCsvButton path="/reports/tender-types/export.csv" filter={filter} isSingleDay={false} testId="export-tenders-csv" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MiniTile
          label="Total Sales"
          value={`$${salesTotal.toFixed(2)}`}
          valueClass="text-2xl font-mono font-semibold"
          testId="tenders-total-sales"
          extra={prevData ? <DeltaBadge current={salesTotal} previous={prevData.sales_total} /> : null}
        />
        <MiniTile
          label="Transactions"
          value={`${txnTotal}`}
          testId="tenders-total-txns"
          extra={prevData ? <DeltaBadge current={txnTotal} previous={prevData.transactions_total} /> : null}
        />
        <MiniTile
          label="Card Decline Rate"
          value={declineRate === null ? "—" : `${declineRate.toFixed(1)}%`}
          tone={card.declined_count > 0 ? "text-amber-700" : ""}
          testId="tenders-decline-rate"
          extra={
            prevData && declineRate !== null && prevDeclineRate !== null ? (
              <DeltaBadge current={declineRate} previous={prevDeclineRate} invertGood />
            ) : null
          }
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground grid grid-cols-12 gap-2">
          <div className="col-span-5">Tender</div>
          <div className="col-span-2 text-right">Txns</div>
          <div className="col-span-2 text-right">Tips</div>
          <div className="col-span-3 text-right">Total</div>
        </div>
        <div className="divide-y divide-border">
          {orderedKeys.map((key) => {
            const b = tenders[key];
            if (!b) return null;
            const Icon = TENDER_ICONS[key] || Receipt;
            const isZero = b.total === 0 && b.count === 0;
            return (
              <div
                key={key}
                className={`px-3 py-2.5 grid grid-cols-12 gap-2 items-center text-sm ${isZero ? "opacity-50" : ""}`}
                data-testid={`tender-row-${key}`}
              >
                <div className="col-span-5 flex items-center gap-2 min-w-0">
                  <Icon size={18} weight="duotone" className="shrink-0 text-muted-foreground" />
                  <div className="truncate font-medium">{b.label}</div>
                  {!isZero && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      · {share(b.total)}%
                    </span>
                  )}
                </div>
                <div className="col-span-2 text-right font-mono">{b.count}</div>
                <div className="col-span-2 text-right font-mono text-muted-foreground">
                  {b.tips > 0 ? `$${b.tips.toFixed(2)}` : "—"}
                </div>
                <div className="col-span-3 text-right font-mono font-semibold">
                  ${b.total.toFixed(2)}
                </div>
              </div>
            );
          })}

          <div data-testid="tender-row-card">
            <button
              type="button"
              onClick={() => setCardOpen((o) => !o)}
              className="w-full px-3 py-2.5 grid grid-cols-12 gap-2 items-center text-sm hover:bg-secondary/30 transition-colors text-left"
              data-testid="tender-card-toggle"
            >
              <div className="col-span-5 flex items-center gap-2 min-w-0">
                {cardOpen ? (
                  <CaretDown size={14} weight="bold" className="shrink-0 text-muted-foreground" />
                ) : (
                  <CaretRight size={14} weight="bold" className="shrink-0 text-muted-foreground" />
                )}
                <CreditCard size={18} weight="duotone" className="shrink-0 text-muted-foreground" />
                <div className="truncate font-medium">{card.label || "Card"}</div>
                {card.total > 0 && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    · {share(card.total)}%
                  </span>
                )}
                {card.declined_count > 0 && (
                  <span
                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 shrink-0"
                    data-testid="tender-card-decline-badge"
                  >
                    {card.declined_count} declined
                  </span>
                )}
              </div>
              <div className="col-span-2 text-right font-mono">{card.count}</div>
              <div className="col-span-2 text-right font-mono text-muted-foreground">
                {card.tips > 0 ? `$${card.tips.toFixed(2)}` : "—"}
              </div>
              <div className="col-span-3 text-right font-mono font-semibold">
                ${card.total.toFixed(2)}
              </div>
            </button>

            {cardOpen && (
              <div
                className="bg-secondary/30 border-t border-border px-3 py-3 space-y-2 text-sm font-mono"
                data-testid="tender-card-breakdown"
              >
                <Row
                  left={`Authorized (${card.approved_count || 0})`}
                  right={`$${(card.approved_total || 0).toFixed(2)}`}
                  testId="tender-card-approved"
                  bold
                />
                <Row
                  left={`Declined (${card.declined_count || 0})`}
                  right={`$${(card.declined_total || 0).toFixed(2)}`}
                  tone={card.declined_count > 0 ? "text-amber-700" : "text-muted-foreground"}
                  testId="tender-card-declined"
                />
                <Row
                  left={`Refunds (${card.refund_count || 0})`}
                  right={`−$${(card.refund_total || 0).toFixed(2)}`}
                  tone={card.refund_count > 0 ? "text-red-600" : "text-muted-foreground"}
                  testId="tender-card-refunds"
                />
                {(card.approved_count || 0) + (card.declined_count || 0) === 0 && (
                  <div className="text-xs text-muted-foreground italic pt-1">
                    No card activity in this window.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Declines flag card-issue trends (lost sales, bad swipes, fraud attempts). Gift Card &amp;
        Rewards rows light up automatically once those tenders are processed.
      </p>
    </div>
  );
};
